import { UseInfiniteQueryResult, InfiniteData } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useIntersectionObserver } from "@uidotdev/usehooks";
import { useEffect, useMemo } from "react";
import { cn } from "@/lib/utils";
import { SuccessFetchListResponse } from "@/services/api";
import { MobileListLoading } from "./components/MobileListLoading";
import { MobileListError } from "./components/MobileListError";
import { MobileListEmpty } from "./components/MobileListEmpty";

export interface MobileListLabels {
  loading?: string;
  loadingMore?: string;
  noItemsFound?: string;
  errorLoading?: string;
  errorGeneric?: string;
}

interface MobileListProps<T extends { id: string }> {
  query: UseInfiniteQueryResult<
    InfiniteData<SuccessFetchListResponse<T>>,
    Error
  >;
  renderItem: (item: T, index: number) => React.ReactNode;
  className?: string;
  loadingComponent?: React.ReactNode;
  emptyComponent?: React.ReactNode;
  errorComponent?: React.ReactNode;
  infiniteScroll?: boolean;
  loadMoreOffset?: number;
  labels?: MobileListLabels;
}

export function MobileList<T extends { id: string }>({
  query,
  renderItem,
  className,
  loadingComponent,
  emptyComponent,
  errorComponent,
  infiniteScroll = true,
  loadMoreOffset = 200,
  labels,
}: MobileListProps<T>) {
  const { t } = useTranslation();

  const {
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isError,
  } = query;

  const [loadMoreRef, entry] = useIntersectionObserver({
    threshold: 0,
    rootMargin: `${loadMoreOffset}px`,
  });

  useEffect(() => {
    if (
      infiniteScroll &&
      entry?.isIntersecting &&
      hasNextPage &&
      !isFetchingNextPage &&
      !query.isFetchNextPageError
    ) {
      fetchNextPage();
    }
  }, [
    infiniteScroll,
    entry?.isIntersecting,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    query.isFetchNextPageError,
  ]);

  const allItems = useMemo(() => {
    const flatItems =
      query.data?.pages?.flatMap(
        (page: SuccessFetchListResponse<T>) => page.items,
      ) ?? [];

    // Deduplicate by id to prevent duplicates during filter changes and query transitions
    const seen = new Set<string>();
    return flatItems.filter((item) => {
      if (seen.has(item.id)) {
        return false;
      }
      seen.add(item.id);
      return true;
    });
  }, [query.data?.pages]);

  if (isLoading) {
    return (
      <div className={className}>
        {loadingComponent || (
          <MobileListLoading
            label={labels?.loading || t("mobileList.loading")}
          />
        )}
      </div>
    );
  }

  // Show error screen only if we have no cached data
  // If we have cached data, show it even if there's an error (offline scenario)
  if (isError && allItems.length === 0) {
    return (
      <div className={className}>
        {errorComponent || (
          <MobileListError
            title={labels?.errorLoading || t("mobileList.errorLoading")}
            message={
              error instanceof Error
                ? error.message
                : labels?.errorGeneric || t("mobileList.errorGeneric")
            }
          />
        )}
      </div>
    );
  }

  if (allItems.length === 0) {
    return (
      <div className={className}>
        {emptyComponent || (
          <MobileListEmpty
            message={labels?.noItemsFound || t("mobileList.noItemsFound")}
          />
        )}
      </div>
    );
  }

  return (
    <div className={cn("space-y-2", className)}>
      {allItems.map((item: T, index: number) => renderItem(item, index))}

      {infiniteScroll && (
        <div ref={loadMoreRef} className="py-4">
          {isFetchingNextPage && (
            <div className="flex justify-center">
              <div className="flex items-center gap-2">
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-blue-600" />
                <span className="text-sm text-gray-500">
                  {labels?.loadingMore || t("mobileList.loadingMore")}
                </span>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
